import { useListUnlockedPrivateSongs } from "@hooks/trpc/useListUnlockedPrivateSongs";
import type { Song } from "@libs/wire";
import { prefetchAccessDetailHeroImage } from "@utils/accesses/access";
import { router } from "expo-router";
import { useEffect, useMemo, useRef } from "react";

type SharedSong = Pick<Song, "accessId" | "albums" | "id" | "private">;

type SharedSongAction =
  | { type: "wait" }
  | { type: "play"; songId: string }
  | { type: "open-access"; accessId: string };

export const resolveSharedSongAction = ({
  song,
  unlockedSongIds,
}: {
  song?: SharedSong;
  unlockedSongIds?: string[];
}): SharedSongAction => {
  if (!song) return { type: "wait" };

  if (song.private && song.accessId) {
    // Entitlements hydrate asynchronously on cold starts. Wait for them before
    // deciding whether this private song should play or show its access sheet.
    if (!unlockedSongIds) return { type: "wait" };

    if (!unlockedSongIds.includes(song.id)) {
      return { type: "open-access", accessId: song.accessId };
    }
  }

  return { type: "play", songId: song.id };
};

export const useSharedSongLink = ({
  onPlay,
  songId,
  songs,
}: {
  onPlay: (songId: string) => void;
  songId?: string;
  songs?: readonly SharedSong[];
}) => {
  const unlockedSongIds = useListUnlockedPrivateSongs();
  const handledSongIdRef = useRef<string | undefined>(undefined);
  const song = useMemo(
    () => songs?.find((candidate) => candidate.id === songId),
    [songId, songs],
  );

  useEffect(() => {
    if (!songId || handledSongIdRef.current === songId) return;

    const action = resolveSharedSongAction({ song, unlockedSongIds });
    if (action.type === "wait") return;

    handledSongIdRef.current = songId;

    if (action.type === "open-access") {
      prefetchAccessDetailHeroImage(song?.albums.at(0)?.image);
      router.push({
        pathname: "/(app)/access/[accessId]",
        params: { accessId: action.accessId },
      });
      return;
    }

    onPlay(action.songId);
  }, [onPlay, song, songId, unlockedSongIds]);
};
